import React, { useMemo, useState } from "react";
import { Button, InputAdornment } from "@mui/material";
import { Add, Search } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import AppTextField from "../../lib/components/AppTextField";
import AppSelectField from "../../lib/components/AppSelectField";
import CustomPagination from "../../lib/components/AppPagination";
import ConfirmationDialog from "../../lib/components/ConfirmationDialog";
import marketplaceService from "../../service/marketplaceService";
import organizationservice from "../../service/organizationservice";
import pathnames from "../../constants/pathnames";
import useDebounce from "../../hooks/useDebounce";
import errorHandler from "../../utils/errorHandler";
import MarketplaceStats from "./components/MarketplaceStats";
import AddonsDataTable from "./components/AddonsDataTable";
import {
  ITEM_STATUS_OPTIONS,
  KIND_OPTIONS,
  computeCatalogStats,
  marketplaceItemPath,
  marketplaceItemEditPath,
  metaOptions,
  normalizePlans,
} from "./utils";
import "./marketplace.scss";

/**
 * B1/B2 catalogue administration.
 */
const MarketplaceAddons = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const searchParams = new URLSearchParams(window.location.search);

  const page = Number(searchParams.get("page") || 1);
  const limit = Number(searchParams.get("limit") || 20);
  const keyword = searchParams.get("keyword") || "";
  const status = searchParams.get("status") || "ALL";
  const kind = searchParams.get("kind") || "ALL";
  const rule = searchParams.get("rule") || "";

  const debouncedKeyword = useDebounce(keyword, 400);

  const [deleteItem, setDeleteItem] = useState(null);

  const metaQuery = useQuery({
    retry: false,
    queryKey: ["marketplace-meta"],
    queryFn: () => marketplaceService.getMeta(),
  });

  const itemsQuery = useQuery({
    retry: false,
    queryKey: ["marketplace-items", page, limit, debouncedKeyword, status, kind, rule],
    queryFn: () =>
      marketplaceService.getItems({
        page,
        limit,
        keyword: debouncedKeyword || undefined,
        status: status !== "ALL" ? status : undefined,
        kind: kind !== "ALL" ? kind : undefined,
        rule: rule || undefined,
      }),
  });

  const catalogStatsQuery = useQuery({
    retry: false,
    queryKey: ["marketplace-items-stats"],
    queryFn: () => marketplaceService.getItems({ page: 1, limit: 200 }),
  });

  const rulesQuery = useQuery({
    retry: false,
    queryKey: ["marketplace-rules", kind],
    queryFn: () =>
      marketplaceService.getRules({
        type: kind !== "ALL" ? kind : undefined,
      }),
  });

  const plansQuery = useQuery({
    retry: false,
    queryKey: ["subscription-plans"],
    queryFn: () => organizationservice.getSubscriptionPlans(),
  });

  const plans = useMemo(() => normalizePlans(plansQuery.data), [plansQuery.data]);
  const rules = useMemo(() => rulesQuery.data || [], [rulesQuery.data]);
  const rulesByCode = useMemo(() => {
    const map = {};
    rules.forEach((r) => {
      map[r.rule] = r;
    });
    return map;
  }, [rules]);

  const items = useMemo(() => itemsQuery.data?.docs || [], [itemsQuery.data]);
  const totalPages = itemsQuery.data?.totalPages || 1;
  const stats = useMemo(
    () => computeCatalogStats(catalogStatsQuery.data?.docs || items),
    [catalogStatsQuery.data, items]
  );

  const kindFilterOptions = useMemo(() => {
    const fromMeta = metaOptions(metaQuery.data?.kinds || metaQuery.data?.kindOptions, []);
    if (fromMeta.length) {
      return [{ value: "ALL", label: "All Kinds" }, ...fromMeta];
    }
    return KIND_OPTIONS;
  }, [metaQuery.data]);

  const ruleFilterOptions = useMemo(
    () => [
      { value: "", label: "All Rules" },
      ...rules.map((r) => ({ value: r.rule, label: r.label })),
    ],
    [rules]
  );

  const invalidateCatalog = () => {
    queryClient.invalidateQueries({ queryKey: ["marketplace-items"] });
    queryClient.invalidateQueries({ queryKey: ["marketplace-items-stats"] });
    queryClient.invalidateQueries({ queryKey: ["marketplace-meta"] });
  };

  const deleteMutation = useMutation({
    mutationFn: (item) => marketplaceService.deleteItem(item.id),
    onError: ({ response }) => errorHandler(response),
    onSuccess: () => {
      toast.success("Item permanently deleted");
      setDeleteItem(null);
      invalidateCatalog();
    },
  });

  const handleChange = (event, key) => {
    const value = event?.target ? event.target.value : event;
    const params = new URLSearchParams(window.location.search);
    if (value === "" || value == null || value === "ALL") params.delete(key);
    else params.set(key, value);
    if (key !== "page") params.set("page", "1");
    navigate(`${pathnames.MARKETPLACE_ADDONS}?${params.toString()}`);
  };

  return (
    <AppLayout>
      <Header
        title="Marketplace Catalogue"
        description="Build sellable add-ons from capability rules, then manage ACTIVE / HIDDEN / RETIRED lifecycle."
      />
      <div className="p-3 bg-light marketplace-page" style={{ minHeight: "100%" }}>
        <div className="d-flex flex-wrap justify-content-end align-items-center gap-2 mb-3">
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate(pathnames.MARKETPLACE_ADDON_CREATE)}
          >
            Add New Add-on
          </Button>
        </div>

        <MarketplaceStats
          stats={stats}
          isLoading={catalogStatsQuery.isPending || catalogStatsQuery.isFetching}
        />

        <div className="marketplace-filter-bar mb-3">
          <div className="row g-2 align-items-center">
            <div className="col-12 col-lg-4">
              <AppTextField
                placeholder="Search by name, code, or description..."
                value={keyword}
                onChange={(e) => handleChange(e, "keyword")}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search className="text-secondary" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <div className="col-6 col-md-3 col-lg-2">
              <AppSelectField
                value={kind}
                onChange={(e) => handleChange(e, "kind")}
                items={kindFilterOptions}
              />
            </div>
            <div className="col-6 col-md-3 col-lg-2">
              <AppSelectField
                value={rule}
                onChange={(e) => handleChange(e, "rule")}
                items={ruleFilterOptions}
              />
            </div>
            <div className="col-6 col-md-3 col-lg-2">
              <AppSelectField
                value={status}
                onChange={(e) => handleChange(e, "status")}
                items={ITEM_STATUS_OPTIONS}
              />
            </div>
            <div className="col-6 col-md-3 col-lg-2 text-md-end">
              <Button variant="text" onClick={() => navigate(pathnames.MARKETPLACE_ADDONS)}>
                Reset
              </Button>
            </div>
          </div>
        </div>

        <div className="marketplace-table-wrap">
          <AddonsDataTable
            items={items}
            plans={plans}
            rulesByCode={rulesByCode}
            isLoading={itemsQuery.isPending || itemsQuery.isFetching}
            page={page}
            limit={limit}
            onView={(item) => navigate(marketplaceItemPath(item.id))}
            onEdit={(item) => navigate(marketplaceItemEditPath(item.id))}
            onDelete={setDeleteItem}
          />
          <div className="px-3">
            <CustomPagination
              page={page}
              count={totalPages}
              limit={limit}
              limitOptions={[10, 20, 50]}
              onPageChange={(_, next) => handleChange(String(next), "page")}
              onLimitChange={(next) => handleChange(String(next), "limit")}
            />
          </div>
        </div>
      </div>

      <ConfirmationDialog
        isOpen={Boolean(deleteItem)}
        type="danger"
        onClose={() => setDeleteItem(null)}
        title="Hard delete item"
        body={`Permanently delete <strong>${deleteItem?.name || ""}</strong>? Only allowed if it has never been purchased.`}
        actionTitle="Delete Forever"
        isLoading={deleteMutation.isPending}
        onSubmit={() => deleteMutation.mutate(deleteItem)}
      />
    </AppLayout>
  );
};

export default MarketplaceAddons;
